import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import KODIAK_REWARDS_ABI from 'abis/KodiakRewards.json'
import XKDK_ABI from 'abis/XKDK.json'
import { ReactComponent as Locked } from 'assets/svg/icons/Locked.svg'
import { ReactComponent as Stopwatch } from 'assets/svg/icons/Stopwatch.svg'
import { ReactComponent as Unlocked } from 'assets/svg/icons/Unlocked.svg'
import { ReactComponent as Wallet } from 'assets/svg/icons/Wallet.svg'
import LineChart from 'components/Analytics/Chart/LineChart'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import CircularProgress from 'components/CircularProgress'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import { LoadingRows } from 'components/Loader/styled'
import PoweredByKodiak from 'components/PoweredByKodiak'
import Row, { AutoRow, RowBetween } from 'components/Row'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import { useStepLineStateUpdate } from 'components/StepLine/context/StepLineContext'
import StepLineModal from 'components/StepLine/StepLineModal'
import TimeBetweenText from 'components/TimeBetweenText'
import { SupportedChainId } from 'config/chains'
import { KDK_ADDRESSES, KODIAK_REWARDS_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import dayjs from 'dayjs'
import { useGetKdkAprQuery } from 'hooks/backend/kdk'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import useStaking, { useDividends } from 'hooks/useStaking'
import useTheme from 'hooks/useTheme'
import { useApiTokenValue, useGetTokenPrice, useGetTokenPrices } from 'hooks/useTokenPrice'
import { useWriteContract } from 'hooks/useTransaction'
import { useVipTier } from 'hooks/useVipTier'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import useCurrencyLogoURIs from 'lib/hooks/useCurrencyLogoURIs'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import ManagerAllocationButtons from 'pages/Dividends/ManageAllocationButtons'
import UserClaimDividends from 'pages/Dividends/UserClaimDividends'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalLink } from 'react-feather'
import { FaAngleDoubleDown, FaAngleDoubleUp } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { Text } from 'rebass'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import type { Abi } from 'viem'
import { useReadContract, useWatchAsset } from 'wagmi'

import { ThemedText } from '../../theme'
import AddIncentivesStep, { AddIncentivesViewStep, useAddIncentivesState } from './AddIncentivesStep'
import ConvertView from './ConvertView'
import RedeemView from './RedeemView'
import * as S from './styleds'
import VestingView from './VestingView'

export default function StakingPage() {
  const [direction, setDirection] = useState<'convert' | 'redeem' | 'vesting'>('convert')
  const [allocationModalOpen, setAllocationModalOpen] = useState<'allocate' | 'deallocate' | false>(false)
  const [incentivesModalOpen, setIncentivesModalOpen] = useState(false)
  const { available, redeeming, allocated, redeems } = useStaking()
  const { totalAllocation, rewardsInfo, nextCycleStartTime, pendingRewardsAmount, cycleDurationSeconds } =
    useDividends()
  const { chainId, account, wagmiPublicClient, isConnected } = useConnection()
  const kdkAddress = KDK_ADDRESSES[chainId]
  const xKdkAddress = XKDK_ADDRESSES[chainId]
  const kdkToken = useToken(kdkAddress)
  const xKdkToken = useToken(xKdkAddress)

  const sum = useMemo(() => sumCurrencyAmount([available, redeeming, allocated]), [available, redeeming, allocated])
  const theme = useTheme()
  const { tier: vipTier, isLoading: isVipTierLoading, isFetched: isVipTierFetched } = useVipTier(account)
  const vipTierLabel = useMemo(() => {
    if (!account) return 'Connect wallet'
    if (isVipTierLoading) return 'Loading...'
    if (isVipTierFetched && !vipTier?.label) return '-'
    return vipTier?.label || '—'
  }, [account, isVipTierLoading, isVipTierFetched, vipTier?.label])

  const vipTierDiscountText = useMemo(() => {
    if (!account || !vipTier?.discountRate) return ''
    const discountPercentage = (vipTier.discountRate * 100).toFixed(0)
    return `- ${discountPercentage}% fees`
  }, [account, vipTier?.discountRate])
  const hasRedeemsLoaded = redeems !== undefined
  const isActiveVesting = (redeems?.length || 0) > 0
  useEffect(() => {
    if (hasRedeemsLoaded && !isActiveVesting && direction === 'vesting') {
      setDirection('redeem')
    }
  }, [hasRedeemsLoaded, isActiveVesting, direction])

  const { data: totalSupplyResult } = useReadContract({
    address: xKdkAddress,
    abi: XKDK_ABI as Abi,
    functionName: 'totalSupply',
    query: {
      enabled: !!xKdkAddress,
    },
  })

  const totalSupply = useMemo(() => {
    if (totalSupplyResult && kdkToken) {
      // totalSupply from xKDK, but return CurrencyAmount for KDK to calculate the price
      return CurrencyAmount.fromRawAmount(kdkToken, totalSupplyResult.toString())
    }
    return undefined
  }, [totalSupplyResult, kdkToken])

  const { value: tvl } = useApiTokenValue(totalSupply)

  const stakingDisabled = chainId !== SupportedChainId.BERA
  const logoURL = useCurrencyLogoURIs(xKdkToken)[0]
  const { watchAsset } = useWatchAsset()

  const rewardTokens = useMemo(() => {
    return pendingRewardsAmount
      .map((amount) => amount?.currency.address)
      .filter((address): address is string => !!address)
  }, [pendingRewardsAmount])

  const { prices } = useGetTokenPrices(rewardTokens)

  const currentEpochRewardTokens = useMemo(() => {
    return rewardsInfo
      .map((info) => info?.currentDistributionAmount?.currency?.address)
      .filter((address): address is string => !!address)
  }, [rewardsInfo])

  const { prices: currentEpochPrices } = useGetTokenPrices(currentEpochRewardTokens)

  const totalRewardsUSD = useMemo(() => {
    if (!prices || !pendingRewardsAmount) return 0

    const validAmounts = pendingRewardsAmount.filter(
      (amount): amount is CurrencyAmount<Token> => !!amount && !!amount.currency.address
    )

    if (validAmounts.length !== prices.length) return 0

    return validAmounts.reduce((acc, amount, index) => {
      const price = prices[index]
      if (typeof price !== 'number') return acc

      const value = parseFloat(amount.toExact()) * price
      return acc + value
    }, 0)
  }, [prices, pendingRewardsAmount])

  const totalCurrentRewardsUSD = useMemo(() => {
    if (!currentEpochPrices || rewardsInfo.length === 0) return 0

    const validAmounts = rewardsInfo
      .map((info) => info?.currentDistributionAmount)
      .filter((amount): amount is CurrencyAmount<Token> => !!amount && !!amount.currency.address)

    if (validAmounts.length !== currentEpochPrices.length) return 0

    return validAmounts.reduce((acc, amount, index) => {
      const price = currentEpochPrices[index]
      if (typeof price !== 'number') return acc

      const value = parseFloat(amount.toExact()) * price
      return acc + value
    }, 0)
  }, [currentEpochPrices, rewardsInfo])

  const now = useMemo(() => Math.floor(Date.now() / 1000), [])

  const endCyclePercentage =
    nextCycleStartTime && cycleDurationSeconds
      ? Math.min(100, Math.max(0, ((now - (nextCycleStartTime - cycleDurationSeconds)) / cycleDurationSeconds) * 100))
      : 0

  const currentCycleStartTime = useMemo(() => {
    if (!nextCycleStartTime || !cycleDurationSeconds) return undefined
    return nextCycleStartTime - cycleDurationSeconds
  }, [nextCycleStartTime, cycleDurationSeconds])

  const epochLengthDays = useMemo(() => {
    const days = cycleDurationSeconds / (24 * 60 * 60)
    return `${Math.round(days)} days`
  }, [cycleDurationSeconds])

  const isBeforeCurrentCycle = currentCycleStartTime ? now < currentCycleStartTime : false
  const epochTimestamp = isBeforeCurrentCycle ? currentCycleStartTime : nextCycleStartTime

  const isActiveHarvest = pendingRewardsAmount.some((amount) => amount !== undefined && amount.greaterThan('0'))
  const [harvesting, setHarvesting] = useState(false)

  const rewardsAddress = KODIAK_REWARDS_ADDRESSES[chainId]
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const harvestRewards = async () => {
    if (!rewardsAddress || !wagmiPublicClient || !account) return
    setHarvesting(true)

    try {
      const tx = {
        address: rewardsAddress,
        abi: KODIAK_REWARDS_ABI as Abi,
        functionName: 'harvestAllRewards',
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })
      addTransaction({ hash: txHash }, { type: TransactionType.DIVIDENDS_HARVEST })

      await waitForTransactionReceipt({
        hash: txHash,
      })
    } catch (error) {
      console.error('Error harvesting rewards:', error)
    } finally {
      setHarvesting(false)
    }
  }

  const startTime = useMemo(() => now - 24 * 60 * 60, [now])

  const { data: aprData } = useGetKdkAprQuery({
    resolution: '1h',
    startTime,
    endTime: now,
  })

  const chartData = useMemo(() => {
    if (!aprData?.history) return []

    return aprData.history.map((item, index, arr) => ({
      time: dayjs.unix(item.time).format('YYYY-MM-DD'),
      value: arr.length - 1 !== index ? item.apr : aprData.currentApr,
      t: item.time * 1000,
      label: dayjs.unix(item.time).format('MMM D'),
      index,
    }))
  }, [aprData])

  const aprChange = useMemo(() => {
    if (!aprData?.history || aprData.history.length < 2) return undefined
    const firstPoint = aprData.history[0].apr
    const lastPoint = aprData.currentApr
    const change = lastPoint - firstPoint

    return Math.abs(change) < 0.01 ? undefined : { value: Math.abs(change), up: change > 0 }
  }, [aprData])

  const { price } = useGetTokenPrice(KDK_ADDRESSES[chainId])
  const totalStakedUsd = useMemo(() => {
    if (!price) return 0
    return (parseFloat(totalAllocation?.toExact() || '0') * price).toFixed(2)
  }, [price, totalAllocation])

  const addToken = useCallback(() => {
    if (!xKdkToken?.symbol) return
    watchAsset(
      {
        type: 'ERC20',
        options: {
          address: xKdkToken.address,
          symbol: xKdkToken.symbol,
          decimals: xKdkToken.decimals,
          image: logoURL,
        },
      },
      {
        onSuccess: () => {
          console.log('Token added to wallet')
        },
        onError: (error) => {
          console.error('Error adding token to wallet:', error)
        },
      }
    )
  }, [watchAsset, logoURL, xKdkToken])

  const incentivesStepUpdate = useStepLineStateUpdate()
  const handleIncentivesBack = useCallback(() => {
    incentivesStepUpdate({
      '__nexts/AddIncentivesViewStep': false,
      '__nexts/add-incentives-step': false,
    })
  }, [incentivesStepUpdate])

  const { amount: incentivesAmount, attemptingTxn, hash, onAddIncentives, reset } = useAddIncentivesState()

  const addIncentivesStep = {
    ...useStepWithNext('AddIncentivesViewStep', AddIncentivesViewStep, {}),
    onBack: handleIncentivesBack,
  }

  const approveIncentivesStep = {
    ...useApproveStep(incentivesAmount, KODIAK_REWARDS_ADDRESSES[chainId], false),
    onBack: handleIncentivesBack,
  }

  const confirmAddIncentivesStep = useStepWithNext('add-incentives-step', AddIncentivesStep, {
    amount: incentivesAmount,
    attemptingTxn,
    pendingText: (
      <>
        Adding incentives {incentivesAmount?.toSignificant(6)} {incentivesAmount?.currency.symbol}
      </>
    ),
    hash,
    autoConfirm: false,
    transactionWaiting: true,
    onAddIncentives,
  })

  const steps = [addIncentivesStep, approveIncentivesStep, confirmAddIncentivesStep]

  const handleOpenIncentives = useCallback(() => {
    reset()
    setIncentivesModalOpen(true)
  }, [reset])

  const handleCloseIncentives = useCallback(() => {
    handleIncentivesBack()
    reset()
    setIncentivesModalOpen(false)
  }, [handleIncentivesBack, reset])

  return (
    <>
      <S.Wrapper>
        <>
          <StepLineModal
            header={<>Add Incentives</>}
            isOpen={incentivesModalOpen}
            onDismiss={handleCloseIncentives}
            onFinish={handleCloseIncentives}
            steps={steps}
          />
          <S.StakingCardFlex>
            <S.AdaptiveFlexRowBetween>
              <S.FlexCol>
                <ThemedText.DeprecatedBody fontSize="18px" fontWeight="700" lineHeight="28px" color={theme.textPrimary}>
                  Token Staking
                </ThemedText.DeprecatedBody>

                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Convert, redeem, and stake your xKDK.
                </ThemedText.DeprecatedBody>
              </S.FlexCol>

              <S.ResponsiveFlexRowCenter>
                <S.TopStatBox>
                  <ThemedText.DeprecatedBody
                    fontSize="14px"
                    fontWeight="400"
                    lineHeight="20px"
                    color={theme.textSecondary}
                  >
                    Total Staked
                  </ThemedText.DeprecatedBody>
                  <ThemedText.DeprecatedBody
                    fontSize="18px"
                    fontWeight="700"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    {formatAmount(parseFloat(totalAllocation?.toExact() || '0'))} xKDK{' '}
                    <span style={{ fontSize: '14px', fontWeight: 500, color: theme.textSecondary }}>
                      ({formatDollarAmount(totalStakedUsd)})
                    </span>
                  </ThemedText.DeprecatedBody>
                </S.TopStatBox>

                <S.TopStatBox>
                  <S.TopStatContent>
                    <ThemedText.DeprecatedBody
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="20px"
                      color={theme.textSecondary}
                    >
                      APR
                    </ThemedText.DeprecatedBody>
                    <S.FlexRowCenter gap="4px">
                      <ThemedText.DeprecatedBody
                        fontSize="18px"
                        fontWeight="700"
                        lineHeight="28px"
                        color={theme.textPrimary}
                      >
                        {aprData ? `${aprData.currentApr.toFixed(2)}%` : '0%'}
                      </ThemedText.DeprecatedBody>
                      {aprChange && (
                        <ThemedText.DeprecatedBody
                          fontSize="12px"
                          fontWeight="600"
                          lineHeight="28px"
                          color={aprChange.up ? theme.success500 : theme.error500}
                        >
                          <Row align="center" gap="2px">
                            {aprChange.up ? <FaAngleDoubleUp /> : <FaAngleDoubleDown />}
                            {formatAmount(aprChange.value, 2)}%
                          </Row>
                        </ThemedText.DeprecatedBody>
                      )}
                    </S.FlexRowCenter>
                  </S.TopStatContent>
                  <S.ChartWrapper>
                    <LineChart
                      gradientOpacity={1}
                      color={aprChange ? (aprChange.up ? theme.success500 : theme.error500) : undefined}
                      data={chartData}
                      height="100%"
                      minHeight={0}
                      summaryProps={{ title: '' }}
                      className="[&>div:first-child]:hidden !p-0 !border-none !bg-transparent !rounded-none !h-full"
                      showTimeSelector={false}
                      showZoomControls={false}
                      hideXAxis
                      hideYAxis
                      domain={['dataMin', 'dataMax']}
                      padding={{ top: 20, bottom: 20 }}
                    />
                  </S.ChartWrapper>
                </S.TopStatBox>
              </S.ResponsiveFlexRowCenter>
            </S.AdaptiveFlexRowBetween>

            <S.LinkRow>
              <S.ExternalLinkButton>
                <Link to={`/swap/${kdkAddress}`} target="_blank">
                  Get KDK
                </Link>
                <ExternalLink size={18} />
              </S.ExternalLinkButton>
              {kdkAddress && chainId && (
                <S.HideOnMobile>
                  <S.ExternalLinkButton>
                    <Link to={getExplorerLink(chainId, kdkAddress, ExplorerDataType.ADDRESS)} target="_blank">
                      KDK Contract
                    </Link>
                    <ExternalLink size={18} />
                  </S.ExternalLinkButton>
                </S.HideOnMobile>
              )}
              {rewardsAddress && chainId && (
                <S.HideOnMobile>
                  <S.ExternalLinkButton>
                    <Link to={getExplorerLink(chainId, rewardsAddress, ExplorerDataType.ADDRESS)} target="_blank">
                      Rewards Contract
                    </Link>
                    <ExternalLink size={18} />
                  </S.ExternalLinkButton>
                </S.HideOnMobile>
              )}
              <S.HideOnMobile>
                <S.ExternalLinkButton>
                  <Link to="#" onClick={addToken}>
                    Add xKDK to Wallet
                  </Link>
                  <ExternalLink size={18} />
                </S.ExternalLinkButton>
              </S.HideOnMobile>
              <S.HideOnMobile>
                <S.ExternalLinkButton>
                  <Link to="https://documentation.kodiak.finance/protocol/perps/vip" target="_blank">
                    VIP Tier Docs
                  </Link>
                  <ExternalLink size={18} />
                </S.ExternalLinkButton>
              </S.HideOnMobile>
            </S.LinkRow>
          </S.StakingCardFlex>

          <S.StakingCardFlex>
            <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textPrimary}>
              Your xKDK
            </ThemedText.DeprecatedBody>
            <S.ResponsiveFlexRowCenter>
              <S.InnerStakingCardFlex>
                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Total xKDK
                </ThemedText.DeprecatedBody>
                <S.FlexRowCenterBetween>
                  <ThemedText.DeprecatedBody
                    fontSize="18px"
                    fontWeight="700"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    {sum?.toSignificant(6) || '0'}
                  </ThemedText.DeprecatedBody>

                  <Wallet height={20} width={20} fill={theme.buttonPrimary} />
                </S.FlexRowCenterBetween>
              </S.InnerStakingCardFlex>

              <S.InnerStakingCardFlex>
                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Available xKDK
                </ThemedText.DeprecatedBody>
                <S.FlexRowCenterBetween>
                  <ThemedText.DeprecatedBody
                    fontSize="18px"
                    fontWeight="700"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    {available?.toSignificant(6) || '0'}
                  </ThemedText.DeprecatedBody>
                  <Unlocked height={16} width={16} color={theme.buttonPrimary} />
                </S.FlexRowCenterBetween>
              </S.InnerStakingCardFlex>
              <S.InnerStakingCardFlex
                style={{ opacity: stakingDisabled ? 0.5 : 1, pointerEvents: stakingDisabled ? 'none' : 'auto' }}
              >
                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Staked xKDK
                </ThemedText.DeprecatedBody>
                <S.FlexRowCenterBetween>
                  <ThemedText.DeprecatedBody
                    fontSize="18px"
                    fontWeight="700"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    {allocated?.toSignificant(6) || '0'}
                  </ThemedText.DeprecatedBody>
                  <Locked height={16} width={16} color={theme.buttonPrimary} />
                </S.FlexRowCenterBetween>
              </S.InnerStakingCardFlex>
              <S.InnerStakingCardFlex
                style={{ opacity: stakingDisabled ? 0.5 : 1, pointerEvents: stakingDisabled ? 'none' : 'auto' }}
              >
                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Redeeming xKDK
                </ThemedText.DeprecatedBody>
                <S.FlexRowCenterBetween>
                  <ThemedText.DeprecatedBody
                    fontSize="18px"
                    fontWeight="700"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    {redeeming?.toSignificant(6) || '0'}
                  </ThemedText.DeprecatedBody>

                  <Stopwatch height={16} width={16} color={theme.buttonPrimary} />
                </S.FlexRowCenterBetween>
              </S.InnerStakingCardFlex>
              <S.VipTierCard>
                {vipTier?.imageUrl && <S.VipTierBadgeTopRight src={vipTier.imageUrl} alt={`${vipTierLabel} badge`} />}
                <ThemedText.DeprecatedBody
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color={theme.textSecondary}
                >
                  Perps VIP Tier
                </ThemedText.DeprecatedBody>
                <S.FlexRowCenterBetween>
                  <ThemedText.DeprecatedBody
                    fontSize="18px"
                    fontWeight="700"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    {vipTierLabel}
                  </ThemedText.DeprecatedBody>
                  {vipTierDiscountText && (
                    <ThemedText.DeprecatedBody
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="28px"
                      color={theme.textSecondary}
                    >
                      ({vipTierDiscountText})
                    </ThemedText.DeprecatedBody>
                  )}
                </S.FlexRowCenterBetween>
              </S.VipTierCard>
            </S.ResponsiveFlexRowCenter>
          </S.StakingCardFlex>

          <S.CardRowFlex>
            <S.StakingCardWrapperFlex>
              <S.StakingCardFlex
                style={{ opacity: stakingDisabled ? 0.5 : 1, pointerEvents: stakingDisabled ? 'none' : 'auto' }}
              >
                <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textPrimary}>
                  xKDK {direction === 'convert' ? 'Conversion' : 'Redemption'} {stakingDisabled ? ' (disabled)' : ''}
                </ThemedText.DeprecatedBody>
                <S.DepositWithdrawSwitch>
                  <S.DepositWithdrawOption
                    onClick={() => {
                      setDirection('convert')
                    }}
                    active={direction === 'convert'}
                  >
                    <>Convert</>
                  </S.DepositWithdrawOption>
                  <S.DepositWithdrawOption
                    onClick={() => {
                      // When switching to custom slippage, use `auto` value as a default.
                      setDirection('redeem')
                    }}
                    active={direction === 'redeem'}
                    tabIndex={-1}
                  >
                    <>Redeem</>
                  </S.DepositWithdrawOption>
                  {isActiveVesting && (
                    <S.DepositWithdrawOption
                      onClick={() => {
                        // When switching to custom slippage, use `auto` value as a default.
                        setDirection('vesting')
                      }}
                      active={direction === 'vesting'}
                      tabIndex={-1}
                    >
                      <>Redemptions</>
                    </S.DepositWithdrawOption>
                  )}
                </S.DepositWithdrawSwitch>

                {direction === 'convert' ? (
                  <ConvertView key="staking-convert" onStake={() => setAllocationModalOpen('allocate')} />
                ) : direction === 'vesting' ? (
                  <VestingView key="staking-vesting" />
                ) : (
                  <RedeemView key="staking-redeem" />
                )}
              </S.StakingCardFlex>
            </S.StakingCardWrapperFlex>
            <S.CardColFlex style={{ width: '100%' }}>
              <S.StakingCardFlex>
                <RowBetween>
                  <Text>Protocol Rewards</Text>
                  <AutoRow width="fit-content">
                    <ButtonPrimary disabled={!isActiveHarvest || harvesting} onClick={harvestRewards}>
                      Claim all ≈ $
                      {totalRewardsUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </ButtonPrimary>
                  </AutoRow>
                </RowBetween>
                {isConnected === false && (
                  <AutoColumn gap="20px">
                    <Text fontSize={14} color={theme.textSecondary}>
                      Connect your wallet to view rewards
                    </Text>
                  </AutoColumn>
                )}
                {pendingRewardsAmount.some((item) => item !== undefined) ? (
                  <>
                    <UserClaimDividends />
                    <ManagerAllocationButtons isOpen={allocationModalOpen} setIsOpen={setAllocationModalOpen} />
                  </>
                ) : (
                  account && (
                    <ManagerAllocationButtons isOpen={allocationModalOpen} setIsOpen={setAllocationModalOpen} />
                  )
                )}
              </S.StakingCardFlex>
              <S.StakingCardFlex>
                {rewardsInfo.length === 0 && (
                  <LightCard>
                    <LoadingRows>
                      <div></div>
                    </LoadingRows>
                  </LightCard>
                )}
                <AutoRow justify="space-between">
                  <Row gap="4px" width="fit-content">
                    <Text fontSize={14}>Current Epoch Rewards</Text>{' '}
                    <Text color={theme.textSecondary} fontSize="14px" fontWeight={500}>
                      (${formatAmount(totalCurrentRewardsUSD, 2)})
                    </Text>
                  </Row>
                  <S.AddIncentivesButton onClick={handleOpenIncentives}>Add Incentives</S.AddIncentivesButton>
                </AutoRow>
                {rewardsInfo ? (
                  <LightCard>
                    <AutoColumn gap="16px">
                      {rewardsInfo.map((info, index) =>
                        info ? (
                          <AutoRow gap="8px" key={index}>
                            <div>
                              <CurrencyLogo size="32px" currency={info?.currentCycleDistributedAmount?.currency} />
                            </div>
                            <Column>
                              <Text fontSize={14} color={theme.textSecondary}>
                                {info?.currentCycleDistributedAmount?.currency?.symbol}
                              </Text>
                              <Row>
                                <Text fontSize={14} color={theme.textPrimary}>
                                  {formatCurrencyAmount(info?.currentDistributionAmount, 4)}
                                </Text>
                                <Text fontSize={11} fontWeight={500} color={theme.textSecondary} marginLeft="4px">
                                  (<FiatValue currencyAmount={info?.currentDistributionAmount} />)
                                </Text>
                              </Row>
                            </Column>
                          </AutoRow>
                        ) : (
                          <LoadingRows key={index}>
                            <div></div>
                          </LoadingRows>
                        )
                      )}
                    </AutoColumn>
                  </LightCard>
                ) : (
                  <></>
                )}
                <Column gap="18px" width="100%">
                  <AutoRow justify="space-between">
                    <Text fontSize={14}>Epoch length </Text>
                    <Text fontSize={14}>{epochLengthDays}</Text>
                  </AutoRow>
                  <AutoRow justify="space-between">
                    <Text fontSize={14}>Next epoch</Text>
                    <Row width="auto" gap="4px" style={{ marginLeft: '3px' }}>
                      <CircularProgress size={16} percentage={endCyclePercentage} />
                      <Text color={theme.textPrimary} fontSize={14}>
                        {epochTimestamp ? <TimeBetweenText date={new Date(epochTimestamp * 1000)} /> : '-'}
                      </Text>
                    </Row>
                  </AutoRow>
                </Column>
              </S.StakingCardFlex>
            </S.CardColFlex>
          </S.CardRowFlex>
        </>
      </S.Wrapper>
      <PoweredByKodiak />
    </>
  )
}
